import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ModuleCatalog } from '../types';

interface Props {
  catalog: ModuleCatalog | null;
  /** Viewport position (px) where the connection was dropped. */
  x: number;
  y: number;
  onPick: (moduleId: number) => void;
  onClose: () => void;
}

const CARD_W = 340;
const CARD_H = 420;

/**
 * Shown when a connection is dropped on empty canvas: pick the module to
 * create there, wired straight to the exit the line came from. A real
 * modal (dimmed backdrop, portal to body); the card sits at the drop point
 * on desktop and is a bottom sheet on phones. Compact pills per category
 * so it reads as a quick choice, not as the palette.
 */
export function ConnectPicker({ catalog, x, y, onPick, onClose }: Props) {
  const [q, setQ] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const groups = useMemo(() => {
    if (!catalog) return [];
    const needle = q.trim().toLowerCase();
    return catalog.categories
      .map((cat) => ({
        cat,
        mods: catalog.modules.filter(
          (m) =>
            m.categoryId === cat.categoryId &&
            (!needle || m.name.toLowerCase().includes(needle) || (m.description ?? '').toLowerCase().includes(needle)),
        ),
      }))
      .filter((g) => g.mods.length > 0);
  }, [catalog, q]);

  // Keep the card inside the viewport; below/right of the drop point when it fits.
  const left = Math.max(8, Math.min(x + 8, window.innerWidth - CARD_W - 8));
  const top = Math.max(8, Math.min(y + 8, window.innerHeight - CARD_H - 8));

  return createPortal(
    <div
      className="fb-connect-picker-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
    <div
      className="fb-connect-picker"
      style={{ left, top }}
      role="dialog"
      aria-modal="true"
      aria-label="Add a module here"
    >
      <div className="fb-connect-picker-hd">
        <span>Add module here</span>
        <button type="button" className="fb-connect-picker-close" onClick={onClose} aria-label="Cancel">
          ×
        </button>
      </div>
      <input
        className="fb-connect-picker-search"
        placeholder="Filter…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoFocus
      />
      <div className="fb-connect-picker-body">
        {groups.length === 0 && <div className="fb-muted">No matches</div>}
        {groups.map(({ cat, mods }) => (
          <div key={cat.categoryId} className="fb-connect-picker-cat">
            <div className="fb-connect-picker-cat-lbl">{cat.name}</div>
            <div className="fb-connect-picker-pills">
              {mods.map((m) => (
                <button
                  key={m.moduleId}
                  type="button"
                  className="fb-connect-picker-pill"
                  onClick={() => onPick(m.moduleId)}
                  title={m.description ?? m.name}
                >
                  <span className="fb-connect-picker-dot" style={{ background: m.uiColor || '#94A3B8' }} />
                  {m.name}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
    </div>,
    document.body,
  );
}
